import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    useState,
    useRef
} from "react";
import {
    TextInput as NativeTextInput,
    type FocusEvent,
    type BlurEvent
} from "react-native";
import type ITextInputProps from "./type";
import {
    type TextInputInstance,
    type ITextInputRef
} from "./type";
import stylesheet, {
    getTextInputClearLabelKey,
    getTextInputType
} from "./stylesheet";
import {
    NCoreUIKitLocalize
} from "../../core/hooks";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type RefForwardingComponent
} from "../../types";
import {
    type FieldInputRenderPropsType,
    type FieldActionType
} from "../fieldControl/type";
import {
    FieldVisibilityIcon,
    FieldClearIcon
} from "../fieldControl/icons";
import {
    getFieldSizeKey
} from "../fieldControl/stylesheet";
import {
    warnOnce
} from "../../utils/development";
import {
    uuid
} from "../../utils";
import FieldControl from "../fieldControl";

const TextInput: RefForwardingComponent<ITextInputRef, ITextInputProps> = ({
    secureTextEntry: secureTextEntryProp,
    rightIcon: RightIconComponentProp,
    onChangeText: onChangeTextProp,
    spreadBehaviour = "baseline",
    isAutoKeyboardDismissOnBlur,
    isShowHideTextButton = true,
    rightIconAccessibilityLabel,
    isShowHintTextIcon = false,
    icon: IconComponentProp,
    hintTextContainerStyle,
    iconAccessibilityLabel,
    contentContainerStyle,
    onFocus: onFocusProp,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    onBlur: onBlurProp,
    customBorderColor,
    hideTextIconStyle,
    isLoading = false,
    customTitleColor,
    rightIconOnPress,
    type = "neutral",
    value: valueProp,
    variant = "text",
    customIconColor,
    cleanIconStyle,
    clearIconStyle,
    customLocalize,
    isCleanEnabled,
    isClearEnabled,
    isShowSubTitle,
    rightIconStyle,
    defaultValue,
    hintTextIcon,
    initialValue,
    customColor,
    customTheme,
    iconOnPress,
    inputStyle,
    isOptional,
    validation,
    iconStyle,
    editable,
    hintText,
    readOnly,
    subTitle,
    style,
    title,
    size,
    id,
    ...props
}, ref) => {
    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const inputRef = useRef<TextInputInstance | null>(null);

    const generatedID = useRef(uuid());

    const [
        value,
        setValue
    ] = useState(initialValue ?? defaultValue ?? "");

    const [
        isValueVisible,
        setIsValueVisible
    ] = useState(false);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const currentValue = valueProp ?? value;

    const currentType = getTextInputType({
        type
    });

    const isCurrentReadOnly = isReadOnly || readOnly === true || editable === false;

    const isCurrentClearEnabled = isClearEnabled ?? isCleanEnabled ?? false;

    const currentVariant = secureTextEntryProp ? "hidden" : variant;

    const isClearActionAvailable = isCurrentClearEnabled && currentVariant === "text" && !isDisabled && !isCurrentReadOnly;

    const isHideTextButtonVisible = currentVariant === "hidden" && isShowHideTextButton;

    const isCleanIconStyleUsed = cleanIconStyle !== undefined;

    const isRightIconPressable = !!rightIconOnPress;

    const isIconPressable = !!iconOnPress;

    const currentSize = getFieldSizeKey({
        isActionable: isCurrentClearEnabled && currentVariant === "text" || isHideTextButtonVisible || isIconPressable || isRightIconPressable,
        pointerType,
        size
    });

    useImperativeHandle(
        ref,
        () => ({
            updateValue,
            cleanText,
            clearText,
            focus,
            blur
        }),
        [
            onChangeTextProp
        ]
    );

    useEffect(() => {
        if(type !== "default") {
            return;
        }

        warnOnce({
            message: "TextInput: type \"default\" is deprecated and will be removed in the next major version. Use \"neutral\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(type !== "question") {
            return;
        }

        warnOnce({
            message: "TextInput: type \"question\" is deprecated and will be removed in the next major version. Use type \"neutral\" with \"isShowHintTextIcon\" instead."
        });
    }, [
        type
    ]);

    useEffect(() => {
        if(isCleanEnabled === undefined) {
            return;
        }

        warnOnce({
            message: "TextInput: \"isCleanEnabled\" is deprecated and will be removed in the next major version. Use \"isClearEnabled\" instead."
        });
    }, [
        isCleanEnabled
    ]);

    useEffect(() => {
        if(!isCleanIconStyleUsed) {
            return;
        }

        warnOnce({
            message: "TextInput: \"cleanIconStyle\" is deprecated and will be removed in the next major version. Use \"clearIconStyle\" instead."
        });
    }, [
        isCleanIconStyleUsed
    ]);

    useEffect(() => {
        if(isAutoKeyboardDismissOnBlur === undefined) {
            return;
        }

        warnOnce({
            message: "TextInput: \"isAutoKeyboardDismissOnBlur\" is deprecated and has no effect. A field that loses focus already closes the keyboard unless focus moves to another field."
        });
    }, [
        isAutoKeyboardDismissOnBlur
    ]);

    useEffect(() => {
        if(!isIconPressable || iconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "TextInput: a pressable \"icon\" (iconOnPress) needs an \"iconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        iconAccessibilityLabel,
        isIconPressable
    ]);

    useEffect(() => {
        if(!isRightIconPressable || rightIconAccessibilityLabel) {
            return;
        }

        warnOnce({
            message: "TextInput: a pressable \"rightIcon\" (rightIconOnPress) needs a \"rightIconAccessibilityLabel\" so screen readers can announce it."
        });
    }, [
        rightIconAccessibilityLabel,
        isRightIconPressable
    ]);

    const focus = () => {
        inputRef.current?.focus();
    };

    const blur = () => {
        inputRef.current?.blur();
    };

    const clearText = () => {
        setValue("");

        if(onChangeTextProp) {
            onChangeTextProp("");
        }
    };

    const cleanText = () => {
        warnOnce({
            message: "TextInput: ref method \"cleanText\" is deprecated and will be removed in the next major version. Use \"clearText\" instead."
        });

        clearText();
    };

    const updateValue = (text: string) => {
        setValue(text);
    };

    const onClearPress = () => {
        clearText();

        focus();
    };

    const onChangeText = (text: string) => {
        const inputValue = text.replace(/\n/g, "");

        if(inputValue !== "" && validation && !validation(inputValue)) {
            return;
        }

        setValue(inputValue);

        if(onChangeTextProp) {
            onChangeTextProp(inputValue);
        }
    };

    const onFocus = (event: FocusEvent) => {
        setIsFocused(true);

        if(onFocusProp) {
            onFocusProp(event);
        }
    };

    const onBlur = (event: BlurEvent) => {
        setIsFocused(false);

        if(onBlurProp) {
            onBlurProp(event);
        }
    };

    const toggleValueVisibility = () => {
        setIsValueVisible((previousState) => !previousState);
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const getStartAction = (): FieldActionType | undefined => {
        if(!IconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: iconAccessibilityLabel,
            icon: IconComponentProp,
            onPress: iconOnPress,
            style: iconStyle,
            customIconColor
        };
    };

    const getEndAction = (): FieldActionType | undefined => {
        if(isClearActionAvailable && (currentValue.length > 0 || !RightIconComponentProp)) {
            return {
                accessibilityLabel: localize(getTextInputClearLabelKey({
                    keyboardType: props.keyboardType,
                    inputMode: props.inputMode
                })),
                style: clearIconStyle ?? cleanIconStyle,
                isVisible: currentValue.length > 0,
                isControllingInput: true,
                onPress: onClearPress,
                icon: FieldClearIcon
            };
        }

        if(isHideTextButtonVisible) {
            return {
                accessibilityLabel: localize("show-password"),
                onPress: toggleValueVisibility,
                icon: FieldVisibilityIcon,
                isPressed: isValueVisible,
                style: hideTextIconStyle,
                isControllingInput: true
            };
        }

        if(!RightIconComponentProp) {
            return undefined;
        }

        return {
            accessibilityLabel: rightIconAccessibilityLabel,
            icon: RightIconComponentProp,
            onPress: rightIconOnPress,
            style: rightIconStyle
        };
    };

    const renderInput = ({
        style: inputDynamicStyle,
        placeholderTextColor,
        accessibilityProps
    }: FieldInputRenderPropsType) => {
        return <NativeTextInput
            {...accessibilityProps}
            {...props}
            secureTextEntry={currentVariant === "hidden" && !isValueVisible}
            editable={!isDisabled && !isCurrentReadOnly}
            placeholderTextColor={placeholderTextColor}
            underlineColorAndroid="transparent"
            onChangeText={onChangeText}
            style={[
                inputStyle,
                stylesheet.input,
                inputDynamicStyle
            ]}
            value={currentValue}
            multiline={false}
            onFocus={onFocus}
            onBlur={onBlur}
            ref={inputRef}
        />;
    };

    return <FieldControl
        isShowHintTextIcon={isShowHintTextIcon || type === "question"}
        hintTextContainerStyle={hintTextContainerStyle}
        customBorderColor={customBorderColor}
        controlStyle={contentContainerStyle}
        inputID={id ?? generatedID.current}
        customTitleColor={customTitleColor}
        spreadBehaviour={spreadBehaviour}
        customLocalize={customLocalize}
        startAction={getStartAction()}
        isReadOnly={isCurrentReadOnly}
        hintTextIcon={hintTextIcon}
        endAction={getEndAction()}
        renderInput={renderInput}
        customColor={customColor}
        customTheme={customTheme}
        subTitle={getSubTitle()}
        isDisabled={isDisabled}
        isRequired={isRequired}
        onFocusRequest={focus}
        isFocused={isFocused}
        isLoading={isLoading}
        hintText={hintText}
        type={currentType}
        size={currentSize}
        title={title}
        style={style}
    />;
};
export default forwardRef(TextInput);
